/**
 * feature-173 T3 — pure wall-time → UTC instant helpers.
 *
 * Block-create POSTs RFC3339 UTC instants built from a salon-local wall clock
 * (civil date + minutes-of-day + IANA tz). Timezone projection is done via
 * Intl.DateTimeFormat only — never manual offset tables — consistent with
 * timeGrid.ts / dayMath.ts (ADR-0027 D4). DST transitions are resolved by a
 * two-pass offset probe (the offset at the naive guess can differ from the
 * offset at the resolved instant across a spring-forward / fall-back).
 */

/**
 * Offset (ms) such that `wallClock = utc + offset` for the given IANA tz at the
 * instant `utcMs`. Derived by formatting the instant in `tz` and reading the
 * wall clock it shows back as if it were UTC.
 */
function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs))
  const get = (type: string): number =>
    Number(parts.find((part) => part.type === type)!.value)
  let hour = get("hour")
  if (hour === 24) hour = 0 // Intl midnight artefact in some implementations
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  )
  return asUtc - utcMs
}

/** RFC3339 UTC string with second precision (no milliseconds). */
function toRfc3339Utc(utcMs: number): string {
  return new Date(utcMs).toISOString().replace(/\.\d{3}Z$/, "Z")
}

/**
 * Projects a salon-local wall time to an RFC3339 UTC instant.
 *
 * @param civilDate    "YYYY-MM-DD" in the salon timezone
 * @param minutesOfDay 0..1440, salon-local wall clock (1440 = next-day 00:00)
 * @param tz           IANA timezone, e.g. "Europe/Riga"
 */
export function wallMinutesToInstant(
  civilDate: string,
  minutesOfDay: number,
  tz: string,
): string {
  const [year, month, day] = civilDate.split("-").map(Number)
  const hours = Math.floor(minutesOfDay / 60)
  const minutes = minutesOfDay % 60
  // Treat the wall clock as if it were UTC, then correct by the tz offset.
  // Date.UTC carries hours >= 24 into the next day (the 1440 boundary case).
  const naiveUtc = Date.UTC(year, month - 1, day, hours, minutes)
  // First pass uses the offset at the naive guess; second pass re-reads the
  // offset at the resolved instant so DST boundaries resolve without drift.
  let utc = naiveUtc - tzOffsetMs(naiveUtc, tz)
  utc = naiveUtc - tzOffsetMs(utc, tz)
  return toRfc3339Utc(utc)
}

/**
 * Builds the `{ startsAt, endsAt }` UTC instant pair for a block from a
 * minutes-of-day range on a civil date in the salon timezone.
 */
export function buildBlockRange(
  civilDate: string,
  range: { start: number; end: number },
  tz: string,
): { startsAt: string; endsAt: string } {
  return {
    startsAt: wallMinutesToInstant(civilDate, range.start, tz),
    endsAt: wallMinutesToInstant(civilDate, range.end, tz),
  }
}
